import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import { withSnackbar } from 'notistack';
import { gql, ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { get, isEmpty } from 'lodash';

// Utilities & styles
import getMessages from '../../../lib/i18n/getMessages';
import { areUsersTheSame, canMessageUser, canViewProfile, bioForUser } from '../../../lib/userUtils';
import client from '../../../lib/apolloClient';
import { trackAnalyticsEvent } from '../../../lib/analytics';
import { closeSuccessionDrawer as closeDrawer } from '../../../actions/successionDrawerActionCreators';
import { findActiveDealInterest, getDealBadge } from './utils/dealUtils';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { LoremIpsum } from 'lorem-ipsum';

// Profile components
import ProfileHeader from './components/ProfileHeader';
import ProfileStats from './components/ProfileStats';
import ProfileCharts from './components/ProfileCharts';
import ProfileSidebar from './components/ProfileSidebar';
import TransitionGoalsCard from './components/TransitionGoalsCard';
import MessagesTab from './components/tabs/MessagesTab';
import DealSection from './components/DealSection';
import PartialSalesSection from './components/PartialSalesSection';
import PracticeDetailsSection from './components/PracticeDetailsSection';
import RjInitiateContactModal from './components/RjInitiateContactModal';

// GraphQL mutations - preserve from existing drawer
const CREATE_CHAT_MUTATION = gql`
  mutation createChat($userName: String!) {
    createChat(userName: $userName) {
      id
    }
  }
`;

const ADD_NOTE_MUTATION = gql`
  mutation addNote($userName: String!, $text: String!) {
    addNote(userName: $userName, text: $text) {
      id
      comment
      created_at
    }
  }
`;

const DELETE_NOTE_MUTATION = gql`
  mutation deleteNote($id: ID!, $userName: String!) {
    deleteNote(id: $id, userName: $userName) {
      id
      comment
      created_at
    }
  }
`;

const REMOVE_FROM_ROLODEX_MUTATION = gql`
  mutation RemoveFromRolodex($userName: String!) {
    deleteAllNotes(userName: $userName)
  }
`;

const GET_NOTES_FOR_USER = gql`
  query NotebookForUser($userName: String!) {
    notesForUser(userName: $userName) {
      id
      comment
      created_at
    }
  }
`;

// Fallback Apollo client creation
const createFallbackClient = () => {
  console.log("Creating fallback Apollo client");
  return new ApolloClient({
    link: new HttpLink({
      uri: '/graphql',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
      },
    }),
    cache: new InMemoryCache(),
  });
};

const SuccessionProfileDrawerNew = ({
  profile,
  closeSuccessionDrawer,
  width,
  location,
  company,
  updateBobCards,
  updateNotes,
  loadBobCardList,
  notes,
  refetch,
  managedMarketplaceOnly = false,
  displaySocialButtons = false,
  disableMessageBtn = false,
  currentUserHasSellingTransitionGoals = false,
  currentUserHasLimitedAccess = false,
  activeDealInterest = null,
  currentUser = null,
  enqueueSnackbar,
  onEditProfile,
  hideMessagesForOwnProfile = false,
  hideNotebookForOwnProfile = false,
}) => {
  // Use succession theme context
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();

  const isFr = () => (window.locale || 'en') === 'fr';
  const t = (en, fr) => isFr() ? fr : en;

  // Refs
  const drawerRef = useRef(null);
  const messagingRef = useRef(null);
  const dealSectionRef = useRef(null);

  // State
  const [isOpen, setIsOpen] = useState(true);
  const [localUserNotes, setLocalUserNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [isInRolodex, setIsInRolodex] = useState(false);
  const [showChatBox, setShowChatBox] = useState(false);
  const [chatId, setChatId] = useState('');
  const [startConvo, setStartConvo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserName, setCurrentUserName] = useState(null);

  // RJ Seller Visibility Workflow — modal state for buyer's one-time initial message.
  const [rjModalOpen, setRjModalOpen] = useState(false);
  const [rjAlreadyInitiated, setRjAlreadyInitiated] = useState(false);

  // Stable intl instance — keeps input focus on re-renders. Was previously built
  // via an inner component that called useIntl(); that component was redefined
  // on every render, causing the whole drawer subtree to unmount/remount on
  // every keystroke (textarea lost focus, slide-in animation replayed).
  const locale = window.locale || 'en';
  const intlCache = useRef(createIntlCache()).current;
  const intl = useMemo(
    () => createIntl(
      { locale, messages: getMessages(locale) },
      intlCache,
    ),
    [locale, intlCache],
  );

  // Set up user data and view permissions
  const user = get(profile, 'data.getUser', {});
  const companyUser = get(profile, 'data.getCurrentUser', {}) || currentUser;
  // company can come from different sources depending on the query; prefer a non-empty object
  const companyFromQuery = get(profile, 'data.company', null) || get(profile, 'data.getCurrentUser.company', null);
  const companyData = !isEmpty(companyFromQuery) ? companyFromQuery : (company || {});
  
  // Fix user name - use allowed_name instead of name
  if (user && !user.name && user.allowed_name) {
    user.name = user.allowed_name;
  }
  
  // Check if viewing own profile
  const isViewingSelf = areUsersTheSame(user, companyUser);
  
  // Determine if we can view full profile details
  const canViewFullProfile = isViewingSelf || canViewProfile(companyUser, user, companyData);
  
  // Determine if profile should be obfuscated
  const isInDealPastShortlistedState = dealInterest && 
    ['shortlisted', 'out_for_receiving_signature', 'awaiting_disclosing_signature', 'nda_completed', 'negotiating'].includes(dealInterest.workflow_state);
  
  const obfuscateView = !isViewingSelf && (
    !companyUser?.is_fb_subscriber ||
    user?.profile_type === 'explicitly_obfuscated_user' ||
    (user?.profile_type === 'implicitly_obfuscated_user' && !isInDealPastShortlistedState) ||
    (!user?.is_pure_buyer && companyUser?.limited_access) ||
    (companyUser?.limited_access && isEmpty(user?.transition_goals))
  );
  
  // Apply obfuscation to sensitive data
  if (obfuscateView) {
    const lorem = new LoremIpsum({
      sentencesPerParagraph: { max: 8, min: 4 },
      wordsPerSentence: { max: 16, min: 4 },
    });
    
    // Replace bio with lorem ipsum
    user.bio = lorem.generateSentences(2);
    
    // Blur financial data (we'll handle this in render with CSS class)
    user._obfuscated = true;
  }
  
  // Debug logging
  console.log('🔍 NEW DRAWER DATA:', {
    userName: user?.name || user?.allowed_name,
    userAllowedName: user?.allowed_name,
    userStatus: user?.status,
    userMotivation: user?.motivation,
    hasCompanyUser: !!companyUser,
    companyUserName: companyUser?.name || companyUser?.allowed_name,
    obfuscateView,
    canViewFullProfile,
    isInDealPastShortlistedState
  });

  // Get notes from multiple sources with proper fallback chain
  let safeUserNotes = [];
  
  // Priority 1: Local state (immediate UI updates)
  if (Array.isArray(localUserNotes) && localUserNotes.length > 0) {
    safeUserNotes = localUserNotes;
  } 
  // Priority 2: User object notes (from GraphQL query)
  else if (user?.notes && Array.isArray(user.notes) && user.notes.length > 0) {
    safeUserNotes = user.notes;
  }
  // Priority 3: Redux state notes
  else if (notes && Array.isArray(notes.notes)) {
    safeUserNotes = notes.notes;
  } 
  else if (notes && notes.notes) {
    safeUserNotes = [notes.notes];
  }
  
  console.log('🔍 ROLODEX DATA:', {
    localUserNotesCount: localUserNotes?.length || 0,
    userNotesCount: user?.notes?.length || 0,
    reduxNotesCount: notes?.notes?.length || 0,
    safeUserNotesCount: safeUserNotes.length,
    userNoteCount: user?.note_count
  });

  const fromDealFlag = typeof window !== 'undefined' && sessionStorage.getItem('profileDrawer_fromDeal') === 'true';
  const storedDealInterest = (() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem('profileDrawer_dealInterest');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('Unable to parse profileDrawer_dealInterest', error);
      return null;
    }
  })();

  // Deal interest.
  //
  // `deal_interest_for_current_user` (inventory_user_type.rb:299-312) is scoped to
  // decks the CURRENT user owns, so a seller viewing a buyer sees only their own
  // deal. It is therefore preferred over the `activeDealInterest` prop, which
  // comes from SuccessionDrawerWithListener and scans the buyer's interests with
  // NO deal-owner scoping — meaning it can surface ANOTHER seller's deal and
  // render that deal's workflow_state here. The prop remains as a fallback for
  // contexts that pass it deliberately (e.g. entering from a deal room).
  const foundDealInterest = (fromDealFlag && storedDealInterest)
    ? storedDealInterest
    : (get(user, 'inventory_user.deal_interest_for_current_user') || activeDealInterest);
  const validDealInterest = foundDealInterest && 
    typeof foundDealInterest === 'object' && 
    (fromDealFlag || (get(foundDealInterest, 'deal') && typeof get(foundDealInterest, 'deal') === 'object')) ? 
    foundDealInterest : null;
  const dealInterest = validDealInterest;

  // Get deal badge for status display
  const dealBadge = validDealInterest ? getDealBadge(validDealInterest, window.locale || 'en') : null;
  
  // Compute status key from deal workflow state or motivation
  // Following legacy drawer logic: only show deal status for ACTIVE deals (not declined/archived)
  const getStatusKey = () => {
    const workflowState = get(validDealInterest, 'workflow_state');

    // Priority 1: Show deal status ONLY if there's an active deal (not declined or archived)
    // This matches the findActiveDealInterest logic which excludes declined/archived states
    if (dealBadge && dealBadge.text && workflowState &&
        workflowState !== 'declined' &&
        workflowState !== 'archived' &&
        workflowState !== 'declined_by_buyer') {
      return null; // will use dealBadge.text directly
    }

    // Priority 2: Show motivation-based status key
    switch (user.motivation) {
      case 'succession':
        return 'succession.profile_drawer.drawer.seeking_succession';
      case 'growth':
        return 'succession.profile_drawer.drawer.seeking_growth';
      case 'no_motivation':
        return 'succession.profile_drawer.drawer.exploring_options';
      default:
        return 'succession.profile_drawer.drawer.available';
    }
  };
  
  // Compute status color - only show deal color for ACTIVE deals
  const getStatusColor = () => {
    const workflowState = get(validDealInterest, 'workflow_state');
    if (dealBadge?.color && workflowState && 
        workflowState !== 'declined' && 
        workflowState !== 'archived' &&
        workflowState !== 'declined_by_buyer') {
      return dealBadge.color;
    }
    
    // Default color based on motivation
    switch (user.motivation) {
      case 'succession':
        return 'orange';
      case 'growth':
        return 'green';
      default:
        return 'blue';
    }
  };
  
  // Add computed status key (resolved to translated text right before render).
  if (user) {
    user.statusKey = getStatusKey();
    user.statusDealBadgeText = (() => {
      const workflowState = get(validDealInterest, 'workflow_state');
      if (dealBadge && dealBadge.text && workflowState &&
          workflowState !== 'declined' &&
          workflowState !== 'archived' &&
          workflowState !== 'declined_by_buyer') {
        return dealBadge.text;
      }
      return null;
    })();
    user.statusColor = getStatusColor();
  }

  // Check visibility flags - use backend's permission system which handles deals, motivations, etc.
  // The backend returns visible_profile_features_for_user array with all allowed features
  const backendAllowsMessage = user?.visible_profile_features_for_user?.includes('message');
  const backendAllowsInviteToDeal = user?.visible_profile_features_for_user?.includes('invite_to_deal');

  // Deal room messaging restrictions: when viewing a user in a deal context,
  // messaging should only be allowed at appropriate deal stages
  const allowShortlistMessaging = get(companyData, 'deal_room_allow_messaging_when_shortlisted', false);
  const dealWorkflowState = get(dealInterest, 'workflow_state');
  const isDealPastShortlisted = dealWorkflowState &&
    ['negotiating', 'out_for_receiving_signature', 'awaiting_disclosing_signature', 'nda_completed'].includes(dealWorkflowState);
  const isDealAtShortlistedWithMessaging = dealWorkflowState === 'shortlisted' && allowShortlistMessaging;

  // If in a deal context, only allow messaging at appropriate stages
  // If not in a deal context, fall back to backend permission
  const dealAllowsMessaging = dealInterest
    ? (isDealPastShortlisted || isDealAtShortlistedWithMessaging)
    : !fromDealFlag; // If opened from deal without context, block messaging

  const showMessagesTab = backendAllowsMessage &&
    dealAllowsMessaging &&
    !disableMessageBtn &&
    !(hideMessagesForOwnProfile && isViewingSelf);
  const showNotebookTab = !(hideNotebookForOwnProfile && isViewingSelf);
  
  console.log('🔍 BUTTON VISIBILITY:', {
    isViewingSelf,
    disableMessageBtn,
    backendAllowsMessage,
    visibleFeatures: user?.visible_profile_features_for_user,
    showMessagesTab,
    showNotebookTab,
    hasCompany: !!company,
    userName: user?.user_name,
    companyUserName: companyUser?.user_name,
    userMotivation: user?.motivation,
    companyUserMotivation: companyUser?.motivation,
    hasDealInterest: !!dealInterest,
    dealWorkflowState,
    allowShortlistMessaging,
    dealAllowsMessaging,
  });

  // Check if user has notes (is in rolodex)
  useEffect(() => {
    setIsInRolodex(safeUserNotes.length > 0);
  }, [safeUserNotes.length]);

  // Initialize local notes from user object on mount or when user changes
  useEffect(() => {
    const userName = user?.user_name;
    
    // If we're viewing a different user, reset local state
    if (userName && userName !== currentUserName) {
      console.log('[INIT_NOTES] User changed from', currentUserName, 'to', userName);
      setCurrentUserName(userName);
      
      // Initialize with user's notes from the server
      if (user?.notes && Array.isArray(user.notes) && user.notes.length > 0) {
        console.log('[INIT_NOTES] Initializing from server notes:', user.notes.length);
        setLocalUserNotes(user.notes);
      } else {
        console.log('[INIT_NOTES] No server notes, clearing local state');
        setLocalUserNotes([]);
      }
    } else if (!currentUserName && user?.notes && Array.isArray(user.notes) && user.notes.length > 0) {
      // Initial load with notes
      console.log('[INIT_NOTES] Initial load with notes:', user.notes.length);
      setCurrentUserName(userName);
      setLocalUserNotes(user.notes);
    }
  }, [user?.user_name, user?.notes, currentUserName]);

  // Initialize chat if there's an existing conversation AND messaging is allowed
  useEffect(() => {
    if (user?.conversation_with_me?.id && showMessagesTab) {
      console.log('[INIT_CHAT] Found existing conversation:', user.conversation_with_me.id);
      setChatId(user.conversation_with_me.id);
      setShowChatBox(true);
    } else if (!showMessagesTab) {
      // If messaging is not allowed, ensure chat box is hidden
      setShowChatBox(false);
    }
  }, [user?.conversation_with_me?.id, showMessagesTab]);

  // Track profile view analytics
  useEffect(() => {
    if (user?.id && !isViewingSelf && typeof window !== 'undefined' && trackAnalyticsEvent) {
      // Entry-point attribution (e.g. 'messages', 'search'). Callers stash this in
      // sessionStorage right before opening; default undefined for direct opens.
      let source;
      try {
        source = sessionStorage.getItem('profileDrawer_source') || undefined;
      } catch (e) {
        // sessionStorage can be unavailable (private mode); attribution is best-effort.
      }
      try {
        trackAnalyticsEvent('Profile View', {
          viewed_user_id: user.id,
          viewed_user_name: user.user_name,
          viewer_user_id: companyUser?.id,
          has_deal_interest: !!dealInterest,
          obfuscated: obfuscateView,
          source
        });
        console.log('[ANALYTICS] Profile view tracked for user:', user.user_name, source ? `(source: ${source})` : '');
      } catch (error) {
        console.error('[ANALYTICS] Failed to track profile view:', error);
      } finally {
        // Clear so a later open from another entry point isn't mis-attributed.
        try { sessionStorage.removeItem('profileDrawer_source'); } catch (e) { /* noop */ }
      }
    }
  }, [user?.id]);

  // SessionStorage context tracking for deal pages
  useEffect(() => {
    // Check if opened from deal page
    const fromDealFlag = sessionStorage.getItem('profileDrawer_fromDeal');
    const focusSection = sessionStorage.getItem('profileDrawer_focusOn');
    
    if (fromDealFlag === 'true' && dealInterest) {
      console.log('[CONTEXT] Opened from deal page, scrolling to deal section');
      
      // Auto-scroll to deal section after a short delay to ensure content is rendered
      setTimeout(() => {
        if (focusSection === 'deal' || focusSection === 'recommendation') {
          dealSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      
      // Clear the flags
      sessionStorage.removeItem('profileDrawer_fromDeal');
      sessionStorage.removeItem('profileDrawer_focusOn');
      sessionStorage.removeItem('profileDrawer_dealInterest');
    }
  }, [dealInterest]);

  // Handle scroll to deal section
  const handleScrollToDeal = () => {
    dealSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Handle escape key to close drawer
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle close
  const handleClose = () => {
    // Clear any sessionStorage flags
    sessionStorage.removeItem('profileDrawer_fromDeal');
    sessionStorage.removeItem('profileDrawer_tab');
    sessionStorage.removeItem('profileDrawer_focusOn');
    sessionStorage.removeItem('profileDrawer_dealInterest');
    
    // Immediately restore body scroll to prevent page from being unclickable
    document.body.style.overflow = 'unset';
    
    setIsOpen(false);
    setTimeout(() => {
      closeSuccessionDrawer();
    }, 300); // Wait for animation to complete
  };

  // Handle click outside to close drawer
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // RJ Seller Visibility Workflow — detect the one-time-initiation flow.
  // Signals: viewer is growth, target is succession and opted in.
  // Backend visible_profile_features_for_user already gates the 'message' token
  // (see user.rb — rj_seller_visibility branch), so if the button is visible
  // AND both sides match, this is our flow.
  const isRjVisibilityFlow = !!(
    companyUser?.motivation === 'growth' &&
    user?.motivation === 'succession' &&
    user?.marketplace_visibility_opt_in
  );

  // Handle messaging - Create chat and show inline at bottom
  const handleMessage = async () => {
    // RJ Seller Visibility Workflow — divert to the one-time confirmation modal.
    if (isRjVisibilityFlow) {
      if (rjAlreadyInitiated) {
        enqueueSnackbar?.('You have already reached out to this advisor.', {
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
        return;
      }
      setRjModalOpen(true);
      return;
    }
    try {
      const apolloClient = client || createFallbackClient();

      console.log('[CREATE_CHAT] Starting chat with:', user.user_name);
      
      // Track analytics
      if (typeof window !== 'undefined' && trackAnalyticsEvent) {
        trackAnalyticsEvent('Message Initiated', {
          target_user_id: user.id,
          target_user_name: user.user_name
        });
      }
      
      const result = await apolloClient.mutate({
        mutation: CREATE_CHAT_MUTATION,
        variables: { userName: user.user_name },
      });

      const newChatId = get(result, 'data.createChat.id');
      if (newChatId) {
        console.log('[CREATE_CHAT] Chat created with ID:', newChatId);
        setChatId(newChatId);
        setShowChatBox(true);
        setStartConvo(false);
        
        enqueueSnackbar?.(t('Chat started successfully', 'Conversation démarrée avec succès'), {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
        
        // Scroll to messaging section
        setTimeout(() => {
          messagingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      enqueueSnackbar?.(t('Failed to start conversation', 'Impossible de démarrer la conversation'), { variant: 'error' });
    }
  };

  // Handle add to rolodex - Add note or remove all notes
  const handleAddToRolodex = async () => {
    try {
      const apolloClient = client || createFallbackClient();
      
      console.log('[ROLODEX] Toggling rolodex for:', user.user_name, 'Currently in:', isInRolodex);
      
      // Track analytics
      if (typeof window !== 'undefined' && trackAnalyticsEvent) {
        trackAnalyticsEvent(isInRolodex ? 'Removed from Rolodex' : 'Added to Rolodex', {
          user_id: user.id,
          user_name: user.user_name
        });
      }
      
      if (isInRolodex) {
        // Remove from rolodex
        await apolloClient.mutate({
          mutation: REMOVE_FROM_ROLODEX_MUTATION,
          variables: { userName: user.user_name },
        });
        
        console.log('[ROLODEX] Removed from rolodex');
        
        // Update local state immediately
        setLocalUserNotes([]);
        setIsInRolodex(false);
        
        enqueueSnackbar?.(t('Removed from rolodex', 'Retiré du Rolodex'), { variant: 'success' });
        
        // Update Redux state if available
        if (updateNotes) {
          updateNotes([]);
        }
      } else {
        // Add to rolodex with a default note
        const result = await apolloClient.mutate({
          mutation: ADD_NOTE_MUTATION,
          variables: {
            userName: user.user_name,
            text: getMessages(window.locale || 'en')['succession.profile_drawer.drawer.added_to_rolodex_note'] || 'Added to Rolodex',
          },
        });
        
        console.log('[ROLODEX] Added to rolodex, result:', result);
        
        // The addNote mutation returns an array of ALL notes for this user
        const allNotes = get(result, 'data.addNote');
        if (allNotes && Array.isArray(allNotes)) {
          console.log('[ROLODEX] Received all notes from server:', allNotes.length);
          setLocalUserNotes(allNotes);
        }
        setIsInRolodex(true);
        
        enqueueSnackbar?.(t('Added to rolodex', 'Ajouté au Rolodex'), { variant: 'success' });
      }
      
      // Refetch user data if available
      if (refetch) {
        refetch();
      }
    } catch (error) {
      console.error('Error updating rolodex:', error);
      enqueueSnackbar?.(t('Failed to update rolodex', 'Impossible de mettre à jour le Rolodex'), { variant: 'error' });
    }
  };

  // Submit a new note
  const submitNote = async () => {
    if (!noteText.trim() || !user?.user_name) {
      console.error('[ADD_NOTE] Cannot add note: empty text or missing username');
      return;
    }
    
    try {
      const apolloClient = client || createFallbackClient();
      
      console.log('[ADD_NOTE] Adding note for user:', user.user_name);
      
      const result = await apolloClient.mutate({
        mutation: ADD_NOTE_MUTATION,
        variables: {
          userName: user.user_name,
          text: noteText,
        },
      });
      
      // Clear the input field immediately
      setNoteText('');
      
      // The addNote mutation returns an array of ALL notes for this user (not just the new one)
      // So we replace our local notes with the complete list from the server
      const allNotes = get(result, 'data.addNote');
      if (allNotes && Array.isArray(allNotes)) {
        console.log('[ADD_NOTE] Received all notes from server:', allNotes.length);
        setLocalUserNotes(allNotes);
      }
      
      enqueueSnackbar?.(t('Note added successfully', 'Note ajoutée avec succès'), { variant: 'success' }); 
      
      // Refetch user data if available
      if (refetch) {
        refetch();
      }
    } catch (error) {
      console.error('[ADD_NOTE] Error adding note:', error);
      enqueueSnackbar?.(t('Failed to add note', "Impossible d'ajouter la note"), { variant: 'error' }); 
    }
  };

  // Delete a note
  const deleteNote = async (noteId) => {
    if (!noteId || !user?.user_name) {
      console.error('[DELETE_NOTE] Cannot delete note: missing ID or username');
      return;
    }
    
    try {
      const apolloClient = client || createFallbackClient();
      
      console.log('[DELETE_NOTE] Deleting note with ID:', noteId);
      
      const result = await apolloClient.mutate({
        mutation: DELETE_NOTE_MUTATION,
        variables: {
          id: noteId,
          userName: user.user_name,
        },
      });
      
      // The deleteNote mutation returns an array of ALL remaining notes for this user
      // So we replace our local notes with the complete list from the server
      const remainingNotes = get(result, 'data.deleteNote');
      if (remainingNotes && Array.isArray(remainingNotes)) {
        console.log('[DELETE_NOTE] Received remaining notes from server:', remainingNotes.length);
        setLocalUserNotes(remainingNotes);
      } else {
        // Fallback: filter locally if server didn't return the notes
        setLocalUserNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
      }
      
      enqueueSnackbar?.(t('Note deleted successfully', 'Note supprimée avec succès'), { variant: 'success' });
      
      // Refetch user data if available
      if (refetch) {
        refetch();
      }
    } catch (error) {
      console.error('[DELETE_NOTE] Error deleting note:', error);
      enqueueSnackbar?.(t('Failed to delete note', 'Impossible de supprimer la note'), { variant: 'error' });
    }
  };

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Don't render anything if drawer is closed
  if (!isOpen && !profile) return null;

  // Resolve status text using the stable intl built above.
  if (user) {
    user.status = user.statusDealBadgeText ||
      (user.statusKey ? intl.formatMessage({ id: user.statusKey }) : intl.formatMessage({ id: 'succession.profile_drawer.drawer.available' }));
  }

  const drawerBody = (
    <>
      {isOpen && (
        <div
          className={`fixed inset-0 z-50 flex ${theme}`}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="flex-1 bg-black/50 backdrop-blur-sm" />

        {/* Drawer */}
        <div
          ref={drawerRef}
          className="w-4/5 bg-succession-bg-primary border-l border-succession-border shadow-2xl overflow-y-auto transition-transform duration-300 ease-out"
          style={{
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
            animation: isOpen ? 'slideFromRight 0.3s cubic-bezier(0.82, 0.085, 0.395, 0.895)' : undefined,
          }}
        >
          {/* Content */}
          <div className="relative">
            {/* Loading Overlay */}
            {(profile.loading || isLoading) && (
              <div className="absolute inset-0 bg-succession-bg-primary bg-opacity-75 z-10 flex items-center justify-center">
                <div className="text-center">
                  <div className="loading-spinner mx-auto mb-4"></div>
                  <p className="text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_drawer.drawer.loading' })}</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {profile.error && (
              <div className="absolute inset-0 bg-succession-bg-primary z-10 flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                  <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
                    {intl.formatMessage({ id: 'succession.profile_drawer.drawer.unable_to_load_profile' })}
                  </h3>
                  <p className="text-sm text-succession-text-muted mb-4">
                    {profile.error.message || intl.formatMessage({ id: 'succession.profile_drawer.drawer.error_loading_profile' })}
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90"
                  >
                    {intl.formatMessage({ id: 'succession.profile_drawer.drawer.close' })}
                  </button>
                </div>
              </div>
            )}

            <div className="p-6 space-y-6">
              {/* Floating Close Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleClose}
                  className="h-10 w-10 hover:bg-succession-bg-secondary rounded-full flex items-center justify-center transition-colors border border-succession-border"
                  aria-label={intl.formatMessage({ id: 'succession.profile_drawer.drawer.close' })}
                >
                  <svg className="w-5 h-5 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Obfuscated View Warning */}
              {obfuscateView && !isViewingSelf && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800 mb-1">{intl.formatMessage({ id: 'succession.profile_drawer.drawer.limited_profile_view' })}</h4>
                      <p className="text-xs text-yellow-700">
                        {intl.formatMessage({ id: 'succession.profile_drawer.drawer.limited_profile_info' })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Self-view message */}
              {isViewingSelf && (
                <div className="bg-succession-primary/10 border border-succession-primary/20 rounded-lg p-4">
                  <p className="text-sm text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.profile_drawer.drawer.self_view_message' })}{' '}
                    <button
                      onClick={onEditProfile}
                      className="text-succession-primary hover:underline font-medium"
                    >
                      {intl.formatMessage({ id: 'succession.profile_drawer.drawer.profile_edit_page' })}
                    </button>
                    .
                  </p>
                </div>
              )}

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left Column - Main Profile Content (3/4 width on desktop) */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Profile Header Card */}
                  <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
                    <ProfileHeader user={user} company={companyData} />
                  </div>

                  {/* Transition Goals Card */}
                  <TransitionGoalsCard user={user} />

                  {/* Stats Card */}
                  <div className={`bg-succession-bg-secondary rounded-lg border border-succession-border p-6 ${obfuscateView ? 'blurry-text' : ''}`}>
                    <ProfileStats user={user} company={companyData} />
                  </div>

                  {/* Charts Card */}
                  <div className={`bg-succession-bg-secondary rounded-lg border border-succession-border p-6 ${obfuscateView ? 'blurry-text' : ''}`}>
                    <ProfileCharts user={user} company={companyData} />
                  </div>

                  {/* Practice Details Section */}
                  {!obfuscateView && <PracticeDetailsSection user={user} company={companyData} />}

                  {/* Partial Sales Section */}
                  {!obfuscateView && canViewFullProfile && (
                    user.partial_sale_listings?.length > 0 || user.open_partial_sale_listings?.length > 0
                  ) && <PartialSalesSection user={user} company={companyData} />}

                  {/* Deal Section */}
                  {dealInterest && !obfuscateView && (
                    <div ref={dealSectionRef}>
                      <DealSection
                        dealInterest={dealInterest}
                        apolloClient={client || createFallbackClient()}
                        onClose={handleClose}
                        company={companyData}
                        currentUser={companyUser}
                      />
                    </div>
                  )}

                  {/* Messaging Section - Inline chat at bottom */}
                  <div ref={messagingRef}>
                    {showChatBox && showMessagesTab && !obfuscateView && (
                      <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6 mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-succession-text-primary">
                            {intl.formatMessage({ id: 'succession.profile_drawer.drawer.messages_with' }, { name: user.name || user.allowed_name })}
                          </h3>
                          <button
                            onClick={() => setShowChatBox(false)}
                            className="text-succession-text-muted hover:text-succession-text-primary transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <MessagesTab
                          showChatBox={showChatBox}
                          chatId={chatId}
                          startConvo={startConvo}
                          createChat={handleMessage}
                          currentUser={companyUser}
                          targetUser={user}
                          apolloClient={client || createFallbackClient()}
                          user={user}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Sidebar (1/4 width on desktop) */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6">
                    <ProfileSidebar
                      user={user}
                      isViewingSelf={isViewingSelf}
                      showMessagesTab={showMessagesTab}
                      showNotebookTab={showNotebookTab}
                      canMessage={showMessagesTab}
                      showInviteToDeal={backendAllowsInviteToDeal}
                      isInRolodex={isInRolodex}
                      onMessage={handleMessage}
                      onAddToRolodex={handleAddToRolodex}
                      userNotes={safeUserNotes}
                      noteText={noteText}
                      onNoteTextChange={setNoteText}
                      onSubmitNote={submitNote}
                      onDeleteNote={deleteNote}
                      currentUser={companyUser}
                      company={companyData}
                      dealInterest={dealInterest}
                      obfuscateView={obfuscateView}
                      apolloClient={client}
                      enqueueSnackbar={enqueueSnackbar}
                      onScrollToDeal={handleScrollToDeal}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
      </>
  );

  return (
    <RawIntlProvider value={intl}>
      <style>
        {`
        @keyframes slideFromRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-from-right {
          animation: slideFromRight 0.3s cubic-bezier(0.82, 0.085, 0.395, 0.895);
        }
        .blurry-text {
          filter: blur(5px);
          user-select: none;
          pointer-events: none;
        }
        .loading-spinner {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
      </style>
      {drawerBody}
      <RjInitiateContactModal
        open={rjModalOpen}
        onClose={() => setRjModalOpen(false)}
        sellerUserId={user?.id}
        sellerName={user?.name || user?.allowed_name}
        onSuccess={(conversationId) => {
          setRjAlreadyInitiated(true);
          enqueueSnackbar?.('Message sent. The advisor will decide whether to respond.', {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }}
        onAlreadyInitiated={() => {
          setRjAlreadyInitiated(true);
          enqueueSnackbar?.('You have already reached out to this advisor.', {
            variant: 'info',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }}
      />
    </RawIntlProvider>
  );
};

SuccessionProfileDrawerNew.propTypes = {
  profile: PropTypes.object.isRequired,
  closeSuccessionDrawer: PropTypes.func.isRequired,
  width: PropTypes.number,
  location: PropTypes.object,
  company: PropTypes.object,
  updateBobCards: PropTypes.func,
  updateNotes: PropTypes.func,
  loadBobCardList: PropTypes.func,
  notes: PropTypes.object,
  refetch: PropTypes.func,
  managedMarketplaceOnly: PropTypes.bool,
  displaySocialButtons: PropTypes.bool,
  disableMessageBtn: PropTypes.bool,
  currentUserHasSellingTransitionGoals: PropTypes.bool,
  currentUserHasLimitedAccess: PropTypes.bool,
  activeDealInterest: PropTypes.object,
  currentUser: PropTypes.object,
  enqueueSnackbar: PropTypes.func,
  onEditProfile: PropTypes.func,
  hideMessagesForOwnProfile: PropTypes.bool,
  hideNotebookForOwnProfile: PropTypes.bool,
};

// Redux connection
const mapStateToProps = (state) => ({
  notes: state.successionDrawer.notes,
});

const mapDispatchToProps = (dispatch) => ({
  closeSuccessionDrawer: () => dispatch(closeDrawer()),
  updateNotes: (notes) => dispatch({ type: 'UPDATE_NOTES', notes }),
});

export default connect(mapStateToProps, mapDispatchToProps)(withSnackbar(SuccessionProfileDrawerNew));

